import { FC } from 'react'
import { Alert, Button, IconButton, Switch, Tooltip, Typography } from '@material-tailwind/react'
import { PiArrowFatDown, PiArrowFatUp, PiImage, PiInfoLight, PiPencil, PiTrash } from 'react-icons/pi'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { CREDITS_ASSETS_BUCKET } from '../utils/constants/db.constants'
import { useBucketInfo } from '../hooks/useBucketInfo'
import { ExperienceCreditsEditorTextBlock } from './ExperienceCreditsEditorTextBlock'
import { ExperienceCreditsEditorImageBlock } from './ExperienceCreditsEditorImageBlock'
import { useDarkMode } from '../contexts/DarkModeContext'

interface CreditBlock {
  id: string
  type: 'text' | 'images'
  text?: string
  images?: Array<{
    id: string
    url: string
    storage_path: string | null
    alt: string
  }>
}

interface ExperienceCreditsEditorProps {
  creditsEnabled: boolean
  creditsBlocks: CreditBlock[]
  onToggleCredits: (enabled: boolean) => void
  onUpdateBlocks: (blocks: CreditBlock[]) => void
  experienceId: string
}

export const ExperienceCreditsEditor: FC<ExperienceCreditsEditorProps> = ({
  creditsEnabled,
  creditsBlocks,
  onToggleCredits,
  onUpdateBlocks,
  experienceId,
}) => {
  const { isLoading: bucketLoading } = useBucketInfo({ bucket: CREDITS_ASSETS_BUCKET, enabled: true })

  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const handleAddBlock = (type: 'text' | 'images') => {
    const newBlock: CreditBlock =
      type === 'text'
        ? { id: uuidv4(), type: 'text', text: '' }
        : { id: uuidv4(), type: 'images', images: [] }
    onUpdateBlocks([...(creditsBlocks ?? []), newBlock])
  }

  return (
    <>
      <div className="flex justify-start items-center gap-2">
        <Typography className="font-bold">{t('experienceCreditsEditor.credits')}</Typography>
        <Tooltip
          content={
            <div className="w-[320px]">
              <Typography variant="small" className="font-bold">
                {t('experienceCreditsEditor.creditsHelp')}
              </Typography>
            </div>
          }
          placement="right"
          interactive
        >
          <span>
            <PiInfoLight className="text-primary dark:text-base-gray-400" />
          </span>
        </Tooltip>
      </div>
      <div className="flex items-center justify-start gap-2">
        <Switch
          color={(darkMode ? 'accent' : 'primary') as colors}
          id="credits_enabled"
          label={t('experienceCreditsEditor.enableCredits')}
          checked={Boolean(creditsEnabled)}
          onChange={(e) => onToggleCredits(e.currentTarget.checked)}
          data-testid="experience-credits-enabled-switch"
        />
      </div>

      {Boolean(creditsEnabled) && (
        <div className="flex flex-col gap-3">
          {(creditsBlocks ?? []).length === 0 && (
            <Alert color={darkMode ? 'blue-gray' : 'blue'} variant={darkMode ? 'filled' : 'ghost'}>
              {t('experienceCreditsEditor.noCredits')}
            </Alert>
          )}

          {(creditsBlocks ?? []).map((block, idx) => {
            const blocksCount = (creditsBlocks ?? []).length

            const moveBlock = (from: number, to: number) => {
              const blocks = [...(creditsBlocks ?? [])]
              if (to < 0 || to >= blocks.length) return
              const [moved] = blocks.splice(from, 1)
              if (!moved) return
              blocks.splice(to, 0, moved)
              onUpdateBlocks(blocks)
            }

            const removeBlock = (blockId: string) => {
              const blocks = (creditsBlocks ?? []).filter((b) => b.id !== blockId)
              onUpdateBlocks(blocks)
            }

            return (
              <div
                key={block.id}
                className="border rounded-md p-3 bg-white-yellow-500 dark:bg-primary-400 dark:border-primary-800"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <Typography variant="h3" className="font-semibold">
                    {block.type === 'text'
                      ? t('experienceCreditsEditor.textBlock')
                      : t('experienceCreditsEditor.imageBlock')}
                  </Typography>
                  <div className="flex items-center gap-1">
                    <IconButton
                      size="sm"
                      variant="text"
                      color={darkMode ? 'white' : 'gray'}
                      onClick={() => moveBlock(idx, idx - 1)}
                      disabled={idx === 0}
                    >
                      <PiArrowFatUp className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="text"
                      color={darkMode ? 'white' : 'gray'}
                      onClick={() => moveBlock(idx, idx + 1)}
                      disabled={idx === blocksCount - 1}
                    >
                      <PiArrowFatDown className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="text"
                      color={darkMode ? 'white' : 'gray'}
                      onClick={() => removeBlock(block.id)}
                    >
                      <PiTrash className="h-4 w-4" />
                    </IconButton>
                  </div>
                </div>

                {block.type === 'text' && (
                  <ExperienceCreditsEditorTextBlock
                    creditsBlocks={creditsBlocks}
                    block={block}
                    onUpdateBlocks={onUpdateBlocks}
                  />
                )}

                {block.type === 'images' && (
                  <ExperienceCreditsEditorImageBlock
                    experienceId={experienceId}
                    creditsBlocks={creditsBlocks}
                    block={block}
                    onUpdateBlocks={onUpdateBlocks}
                  />
                )}
              </div>
            )
          })}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              color={(darkMode ? 'accent' : 'primary') as colors}
              variant="filled"
              className="flex items-center gap-2 uppercase"
              onClick={() => handleAddBlock('text')}
            >
              <PiPencil /> {t('experienceCreditsEditor.addTextBlock')}
            </Button>
            <Button
              size="sm"
              color={(darkMode ? 'accent' : 'primary') as colors}
              variant="filled"
              className="flex items-center gap-2 uppercase"
              onClick={() => handleAddBlock('images')}
              disabled={bucketLoading}
              loading={bucketLoading}
            >
              <PiImage /> {t('experienceCreditsEditor.addImageBlock')}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
